import React from 'react';
import { ExternalLink, Shield, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { apiStatus } = useCurrency();
  const { theme, isDark } = useTheme();

  return (
    <footer 
      className="border-t mt-8 transition-colors duration-300"
      style={{ backgroundColor: theme.bg, borderColor: theme.border }}
    >
      {/* Disclaimer */}
      <div className="border-b py-4" style={{ borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4">
          <div 
            className="flex items-start gap-3 rounded-lg p-4"
            style={{ backgroundColor: theme.bgTertiary }}
          >
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed" style={{ color: theme.textSecondary }}>
              <span className="text-yellow-500 font-semibold">Disclaimer:</span> The data on this page is for informational purposes only and does not constitute investment advice. 
              Prices may be delayed and may differ from actual market values. 
              Always consult authorized institutions before making investment decisions.
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-[#d4af37] to-[#b8960c] rounded-lg flex items-center justify-center">
                <span className="text-black font-bold text-lg">L</span>
              </div>
              <div>
                <h3 className="text-[#d4af37] font-bold text-lg">LETSGOLD.IO</h3>
                <p className="text-xs" style={{ color: theme.textMuted }}>Live Prices</p>
              </div>
            </div>
            <p className="text-sm" style={{ color: theme.textMuted }}>
              Real-time precious metals and currency tracking platform.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-sm" style={{ color: theme.text }}>Quick Access</h4>
            <ul className="space-y-2">
              {['Gold Prices', 'Silver Prices', 'Platinum', 'Palladium'].map((link) => (
                <li key={link}>
                  <a 
                    href="#" 
                    className="text-sm transition-colors hover:text-[#d4af37]"
                    style={{ color: theme.textSecondary }}
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Data Sources */}
          <div>
            <h4 className="font-semibold mb-4 text-sm" style={{ color: theme.text }}>Data Sources</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm" style={{ color: theme.textSecondary }}>
                {apiStatus.gold ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-yellow-500" />}
                <span>Gold-API.com</span>
              </li>
              <li className="flex items-center gap-2 text-sm" style={{ color: theme.textSecondary }}>
                {apiStatus.currency ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-yellow-500" />}
                <span>Frankfurter API</span>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="font-semibold mb-4 text-sm" style={{ color: theme.text }}>Information</h4>
            <ul className="space-y-2 text-sm" style={{ color: theme.textSecondary }}>
              <li>Real-time market data</li>
              <li>Multi-currency support</li>
              <li className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span>SSL Protected</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t py-4" style={{ borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: theme.textMuted }}>
            © {currentYear} LetsGold.io. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs transition-colors hover:text-[#d4af37]" style={{ color: theme.textMuted }}>Privacy Policy</a>
            <a href="#" className="text-xs transition-colors hover:text-[#d4af37]" style={{ color: theme.textMuted }}>Terms of Use</a>
            <a 
              href="https://gold-api.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs flex items-center gap-1 transition-colors hover:text-[#d4af37]"
              style={{ color: theme.textMuted }}
            >
              <ExternalLink className="w-3 h-3" />
              Gold API
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
